/**
 * Wave Feature Image mobile mask fix.
 *
 * Chrome unreliably rasterizes this block's mobile CSS mask when its
 * mask-size depends on viewport/container-relative units (vw or cqw
 * inside the --wave-height calc) — the media renders invisible on first
 * paint and often stays that way. Pixel-based mask geometry rasterizes
 * correctly every time, so measure the element and set --wave-height in
 * px, keeping the stylesheet's cqw value as a no-JS fallback.
 *
 * Wave aspect ratio: 418w x 50h (matches the SVG mask and the CSS calc).
 */
export default function waveMaskRepaint(): void {
  // Chrome's first rasterization of the mask is broken no matter what;
  // only a geometry CHANGE after it re-rasters correctly. The epsilon
  // makes every call produce a slightly different (sub-pixel) value so
  // the post-load call always invalidates the initial broken raster.
  let bump = 0;

  const setWaveHeights = (): void => {
    bump += 1;
    document
      .querySelectorAll<HTMLElement>('.wave-feature-image__media')
      .forEach((el) => {
        const width = el.getBoundingClientRect().width;
        if (width > 0) {
          el.style.setProperty(
            '--wave-height',
            `${(width * 50) / 418 + bump * 0.01}px`
          );
        }
      });
  };

  const bumpNextFrame = (): void => {
    requestAnimationFrame(() => requestAnimationFrame(setWaveHeights));
  };

  setWaveHeights();

  // The broken raster happens when the mask is applied before the media
  // has decoded — re-raster as soon as each media element is ready.
  document
    .querySelectorAll<HTMLElement>('.wave-feature-image__media')
    .forEach((el) => {
      if (el instanceof HTMLImageElement) {
        if (el.complete) {
          (el.decode ? el.decode().catch(() => undefined) : Promise.resolve())
            .then(bumpNextFrame);
        } else {
          el.addEventListener('load', bumpNextFrame, { once: true });
        }
      } else if (el instanceof HTMLVideoElement) {
        if (el.readyState >= 2) {
          bumpNextFrame();
        } else {
          el.addEventListener('loadeddata', bumpNextFrame, { once: true });
        }
      }
    });

  // Backstops.
  window.addEventListener('load', bumpNextFrame);
  window.addEventListener('pageshow', bumpNextFrame);

  let resizeTimer: number | undefined;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(setWaveHeights, 100);
  });
}
